<script>
import InformationLayout from "@/shared/ui/information-layout/InformationLayout.vue";
import { FormItem, FormLabel, FormControl } from "@/shared/ui/form";
import { Input } from "@/shared/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/select";
import { Popover, PopoverTrigger, PopoverContent } from "@/shared/ui/popover";

const VISIBLE_ROLES_LIMIT = 3;

export default {
  name: "DataUser",
  components: {
    InformationLayout,
    FormItem,
    FormLabel,
    FormControl,
    Input,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    Popover,
    PopoverTrigger,
    PopoverContent,
  },
  props: {
    form: {
      type: Object,
      default: () => ({}),
    },
    roles: {
      type: Array,
      default: () => [],
    },
    organizations: {
      type: Array,
      default: () => [],
    },
    selectedUser: {
      type: Object,
      default: () => null,
    },
  },
  data() {
    return {
      loading: false,
      errors: {},
    };
  },
  computed: {
    uuid() {
      return this.$route.params.id;
    },
    showForm() {
      return (
        this.$route.name === "user_new" || this.$route.name === "user_item"
      );
    },
    organizationValue: {
      get() {
        return this.form.organization_id != null
          ? String(this.form.organization_id)
          : undefined;
      },
      set(val) {
        this.form.organization_id = val ? Number(val) : null;
      },
    },
    selectedRoleCodes() {
      return this.form?.role_codes || [];
    },
    visibleRoleCodes() {
      return this.selectedRoleCodes.slice(0, VISIBLE_ROLES_LIMIT);
    },
    hiddenRoleCodes() {
      return this.selectedRoleCodes.slice(VISIBLE_ROLES_LIMIT);
    },
    sections() {
      if (!this.selectedUser) return [];
      return [
        {
          title: this.$t("Данные пользователя"),
          items: [
            {
              label: this.$t("ФИО"),
              values: [{ text: this.getFullName(this.selectedUser) }],
            },
            {
              label: this.$t("ИИН"),
              values: [{ text: this.selectedUser.identifier }],
            },
            {
              label: "E-mail",
              values: [
                { text: this.selectedUser.email || this.$t("Не указано") },
              ],
            },
            {
              label: this.$t("Контактный телефон"),
              values: [
                { text: this.selectedUser.phone || this.$t("Не указано") },
              ],
            },
            {
              label: this.$t("Наименование организации"),
              values: [
                {
                  text: this.selectedUser.organization_id
                    ? this.getOrganizationName(
                        this.selectedUser.organization_id
                      )
                    : this.$t("Не указано"),
                },
              ],
            },
            {
              label: this.$t("Структурное подразделение"),
              values: [{ text: "test" }],
            },
            {
              label: this.$t("Роль в системе"),
              values: [
                {
                  text: this.selectedUser?.role_codes?.length
                    ? this.getRoleName(this.selectedUser.role_codes)
                    : this.$t("Не указано"),
                },
              ],
            },
          ],
        },
      ];
    },
  },
  methods: {
    async validate() {
      const errors = {};
      const form = this.form || {};

      if (!form.last_name?.trim()) errors.last_name = true;
      if (!form.first_name?.trim()) errors.first_name = true;

      const identifier = (form.identifier || "").trim();
      if (!identifier) {
        errors.identifier = true;
      } else if (!/^\d{12}$/.test(identifier)) {
        errors.identifier = true;
      }

      const email = (form.email || "").trim();
      if (!email) {
        errors.email = true;
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        errors.email = true;
      }

      const phone = String(form.phone || "").trim();
      if (!phone || !/^\+7\d{10}$/.test(phone)) errors.phone = true;
      if (!form.organization_id) errors.organization_id = true;
      if (!form.role_codes?.length) errors.role_codes = true;

      this.errors = errors;
      return Object.keys(errors).length === 0;
    },
    clearError(field) {
      if (!this.errors) this.errors = {};
      if (this.errors[field]) {
        delete this.errors[field];
      }
    },
    onNameKeypress(e) {
      if (!/[\p{L}-]/u.test(e.key)) e.preventDefault();
    },
    onDigitKeypress(e) {
      if (!/[0-9]/.test(e.key)) e.preventDefault();
    },
    onNamePaste(field, e) {
      e.preventDefault();
      const text = (e.clipboardData?.getData("text") || "").replace(
        /[^\p{L}-]/gu,
        ""
      );
      this.form[field] = (this.form[field] || "") + text;
      this.clearError(field);
    },
    onDigitPaste(field, e, maxLength) {
      e.preventDefault();
      let text = (
        (this.form[field] || "") + (e.clipboardData?.getData("text") || "")
      ).replace(/\D/g, "");
      if (maxLength) text = text.slice(0, maxLength);
      this.form[field] = text;
      this.clearError(field);
    },
    formatPhone(raw) {
      let digits = String(raw ?? "").replace(/\D/g, "");
      if (digits.startsWith("8") || digits.startsWith("7")) {
        digits = digits.slice(1);
      }
      digits = digits.slice(0, 10);
      return "+7" + digits;
    },
    onPhoneFocus() {
      if (!this.form.phone || !String(this.form.phone).startsWith("+7")) {
        this.form.phone = this.formatPhone(this.form.phone);
      }
    },
    onPhoneInput(e) {
      const formatted = this.formatPhone(e.target.value);
      this.form.phone = formatted;
      e.target.value = formatted;
      this.clearError("phone");
    },
    onPhonePaste(e) {
      e.preventDefault();
      const merged =
        String(this.form.phone || "") +
        (e.clipboardData?.getData("text") || "");
      this.form.phone = this.formatPhone(merged);
      this.clearError("phone");
    },
    toggleRole(code) {
      if (!this.form.role_codes) {
        this.form.role_codes = [];
      }
      const index = this.form.role_codes.indexOf(code);
      if (index === -1) {
        this.form.role_codes.push(code);
      } else {
        this.form.role_codes.splice(index, 1);
      }
      this.clearError("role_codes");
    },
    removeRole(code) {
      if (!this.form.role_codes) return;
      const index = this.form.role_codes.indexOf(code);
      if (index !== -1) {
        this.form.role_codes.splice(index, 1);
      }
    },
    isRoleSelected(code) {
      return this.form.role_codes?.includes(code) ?? false;
    },
    getRoleNameByCode(code) {
      return this.roles.find((r) => r.code === code)?.name || code;
    },
    getFullName(user) {
      const { first_name, last_name, middle_name } = user;
      let full_name = `${first_name} ${last_name}`;
      if (middle_name) {
        full_name = `${first_name} ${last_name} ${middle_name}`;
      }
      return full_name;
    },
    getOrganizationName(id) {
      return this.organizations.find((org) => org.id === id)?.name;
    },
    getRoleName(roleCodes) {
      if (!this.roles || !this.roles.length) {
        return this.$t("Роли не найдены");
      }
      return roleCodes
        .map((code) => {
          const role = this.roles.find((r) => r.code === code);
          return role?.name;
        })
        .filter(Boolean)
        .join(", ");
    },
  },
};
</script>

<template>
  <div class="block">
    <div v-if="showForm" class="flex flex-col gap-6 w-full">
      <div class="flex flex-col gap-3">
        <div class="text-foreground text-lg font-semibold leading-6">
          {{ $t("Данные пользователя") }}
        </div>
        <div class="text-muted-foreground text-sm font-medium leading-5">
          {{ $t("Заполните обязательные поля") }}
        </div>
      </div>

      <div class="flex flex-col gap-3">
        <form class="flex flex-col gap-1" @submit.prevent>
          <FormItem float>
            <FormLabel :class="{ 'label-error': errors?.last_name }">
              {{ $t("Фамилия") }} <span class="required-star">*</span>
            </FormLabel>
            <FormControl>
              <Input
                v-model="form.last_name"
                :placeholder="$t('Фамилия')"
                :class="{ 'input-error': errors?.last_name }"
                @keypress="onNameKeypress"
                @paste="onNamePaste('last_name', $event)"
                @input="clearError('last_name')"
              />
            </FormControl>
          </FormItem>

          <FormItem float>
            <FormLabel :class="{ 'label-error': errors?.first_name }">
              {{ $t("Имя") }} <span class="required-star">*</span>
            </FormLabel>
            <FormControl>
              <Input
                v-model="form.first_name"
                :placeholder="$t('Имя')"
                :class="{ 'input-error': errors?.first_name }"
                @keypress="onNameKeypress"
                @paste="onNamePaste('first_name', $event)"
                @input="clearError('first_name')"
              />
            </FormControl>
          </FormItem>

          <FormItem float>
            <FormLabel>{{ $t("Отчество") }}</FormLabel>
            <FormControl>
              <Input
                v-model="form.middle_name"
                :placeholder="$t('Отчество')"
                @keypress="onNameKeypress"
                @paste="onNamePaste('middle_name', $event)"
              />
            </FormControl>
          </FormItem>

          <FormItem float>
            <FormLabel :class="{ 'label-error': errors?.identifier }">
              {{ $t("ИИН") }} <span class="required-star">*</span>
            </FormLabel>
            <FormControl>
              <Input
                v-model="form.identifier"
                :placeholder="$t('ИИН')"
                maxlength="12"
                inputmode="numeric"
                :class="{ 'input-error': errors?.identifier }"
                @keypress="onDigitKeypress"
                @paste="onDigitPaste('identifier', $event, 12)"
                @input="clearError('identifier')"
              />
            </FormControl>
          </FormItem>

          <FormItem float>
            <FormLabel :class="{ 'label-error': errors?.email }">
              E-mail <span class="required-star">*</span>
            </FormLabel>
            <FormControl>
              <Input
                v-model="form.email"
                placeholder="E-mail"
                :class="{ 'input-error': errors?.email }"
                @input="clearError('email')"
              />
            </FormControl>
          </FormItem>

          <FormItem float>
            <FormLabel :class="{ 'label-error': errors?.phone }">
              {{ $t("Контактный телефон") }}
              <span class="required-star">*</span>
            </FormLabel>
            <FormControl>
              <Input
                v-model="form.phone"
                :placeholder="$t('Телефон')"
                inputmode="numeric"
                maxlength="12"
                :class="{ 'input-error': errors?.phone }"
                @focus="onPhoneFocus"
                @keypress="onDigitKeypress"
                @paste="onPhonePaste"
                @input="onPhoneInput"
              />
            </FormControl>
          </FormItem>

          <FormItem float>
            <FormLabel :class="{ 'label-error': errors?.organization_id }">
              {{ $t("Наименование организации пользователя") }}
              <span class="required-star">*</span>
            </FormLabel>
            <FormControl>
              <Select
                v-model="organizationValue"
                @update:model-value="clearError('organization_id')"
              >
                <SelectTrigger
                  class="w-full"
                  :class="{ 'input-error': errors?.organization_id }"
                  @clear="organizationValue = undefined"
                >
                  <SelectValue :placeholder="$t('Выберите')" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem
                    v-for="item in organizations"
                    :key="item.id"
                    :value="String(item.id)"
                  >
                    {{ item.name }}
                  </SelectItem>
                </SelectContent>
              </Select>
            </FormControl>
          </FormItem>

          <FormItem float>
            <FormLabel :class="{ 'label-error': errors?.role_codes }">
              {{ $t("Роль в системе") }} <span class="required-star">*</span>
            </FormLabel>
            <FormControl>
              <div class="multi-select-wrapper">
                <div
                  class="multi-select-trigger"
                  :class="{ 'input-error': errors?.role_codes }"
                  :data-filled="selectedRoleCodes.length ? '' : undefined"
                  tabindex="0"
                  @click="$refs.roleDropdown?.classList.toggle('hidden')"
                  @blur="
                    setTimeout(
                      () => $refs.roleDropdown?.classList.add('hidden'),
                      200
                    )
                  "
                >
                  <div
                    v-if="selectedRoleCodes.length"
                    class="flex flex-wrap items-center gap-1"
                  >
                    <div
                      v-for="code in visibleRoleCodes"
                      :key="code"
                      class="inline-flex items-center gap-1 rounded-md bg-muted px-2 py-0.5 text-xs font-medium text-foreground"
                    >
                      {{ getRoleNameByCode(code) }}
                      <button
                        type="button"
                        class="ml-0.5 text-muted-foreground hover:text-foreground"
                        @mousedown.prevent.stop="removeRole(code)"
                      >
                        &times;
                      </button>
                    </div>
                    <Popover v-if="hiddenRoleCodes.length">
                      <PopoverTrigger as-child>
                        <button
                          type="button"
                          class="role-more-btn"
                          @click.stop
                          @mousedown.stop
                        >
                          +{{ hiddenRoleCodes.length }}
                        </button>
                      </PopoverTrigger>
                      <PopoverContent
                        align="start"
                        class="w-60 p-2"
                        @click.stop
                        @mousedown.stop
                      >
                        <div class="flex flex-col gap-1">
                          <div
                            v-for="code in hiddenRoleCodes"
                            :key="code"
                            class="inline-flex items-center justify-between gap-1 rounded-md bg-muted px-2 py-0.5 text-xs font-medium text-foreground"
                          >
                            <div class="truncate">
                              {{ getRoleNameByCode(code) }}
                            </div>
                            <button
                              type="button"
                              class="text-muted-foreground hover:text-foreground"
                              @mousedown.prevent.stop="removeRole(code)"
                            >
                              &times;
                            </button>
                          </div>
                        </div>
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
                <div ref="roleDropdown" class="multi-select-dropdown hidden">
                  <div
                    v-for="item in roles"
                    :key="item.code"
                    class="multi-select-option"
                    @mousedown.prevent="toggleRole(item.code)"
                  >
                    <div
                      class="mr-2 h-4 w-4 rounded-sm border border-primary flex items-center justify-center"
                      :class="{
                        'bg-primary text-primary-foreground': isRoleSelected(
                          item.code
                        ),
                      }"
                    >
                      <svg
                        v-if="isRoleSelected(item.code)"
                        xmlns="http://www.w3.org/2000/svg"
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="3"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      >
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    </div>
                    <div
                      class="text-sm text-muted-foreground hover:text-foreground"
                    >
                      {{ item.name }}
                    </div>
                  </div>
                </div>
              </div>
            </FormControl>
          </FormItem>
        </form>
      </div>
    </div>
    <div v-if="$route.name === 'user_item_view'">
      <InformationLayout :showHeader="false" :sections="sections" />
    </div>
  </div>
</template>

<style scoped lang="scss">
.block {
  @apply h-full w-1/2 overflow-auto bg-card rounded-[var(--radius)] p-8;
}

@media (max-width: 1400px) {
  .block {
    @apply w-full;
  }
}

.input-error {
  @apply border-red-500;
}

.label-error {
  @apply text-red-500;
}

.required-star {
  @apply text-red-500 ml-0.5;
}

.multi-select-wrapper {
  @apply relative w-full;
}

.multi-select-trigger {
  @apply flex min-h-9 w-full items-center rounded-md border border-input bg-transparent px-3 py-1 text-sm cursor-pointer;
  @apply focus-visible:border-primary focus-visible:ring-ring/50 focus-visible:ring-[3px];
  @apply transition-[color,box-shadow] outline-none;
}

.multi-select-dropdown {
  @apply absolute z-50 mt-1 w-full rounded-md border bg-popover p-1 shadow-md;
  max-height: 200px;
  overflow-y: auto;
}

.multi-select-option {
  @apply flex items-center rounded-sm px-2 py-1.5 cursor-pointer;
  @apply hover:bg-accent hover:text-accent-foreground;
}

.role-more-btn {
  @apply inline-flex items-center justify-center border border-border bg-muted text-muted-foreground rounded-md;
  height: 22px;
  min-width: 28px;
  padding: 0 6px;
  font-size: 12px;
  font-weight: 500;
  line-height: 1;
  cursor: pointer;

  &:hover {
    @apply text-blue-500 border-blue-200 bg-blue-50;
  }
}
</style>
